import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  GiftIcon,
  KeyIcon,
  PaintBrushIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { api, useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import AdminSystemConfig from '../dashboards/AdminSystemConfig';
import RewardsConfig from '../dashboards/RewardsConfig';
import PlatformCompaniesView from './PlatformCompaniesView';

function PlatformSettingsPanel({ companies = [], companiesLoading = false, initialTab = 'account', initialCompanyId = null, onCompanyConfigured, onNavigateView }) {
  const { user, refreshUser } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const pa = t.platformAdmin;
  const st = t.settings;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [settings, setSettings] = useState({
    system: {
      name: user?.username || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      weeklyReports: true,
      newReferrals: true,
      conversions: true,
    },
    privacy: {
      publicProfile: false,
      showStats: true,
      allowMessages: true,
      dataSharing: false,
      optOutRight: false,
    },
    appearance: {
      language,
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
    },
  });

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (initialCompanyId) {
      setSelectedCompanyId(initialCompanyId);
    }
  }, [initialCompanyId]);

  useEffect(() => {
    let cancelled = false;
    const loadPrivacySettings = async () => {
      try {
        const response = await api.get('/user/privacy-settings');
        if (cancelled) return;
        if (response.data?.privacy) {
          setSettings((prev) => ({
            ...prev,
            privacy: { ...prev.privacy, ...response.data.privacy },
          }));
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.error || st.failedUpdate);
        }
      } finally {
        if (!cancelled) setPrivacyLoading(false);
      }
    };
    void loadPrivacySettings();
    return () => { cancelled = true; };
  }, [st.failedUpdate]);

  const tabs = [
    { id: 'account', name: st.systemSettings, icon: KeyIcon },
    { id: 'profile', name: st.profile, icon: UserIcon },
    { id: 'companies', name: pa.companiesSettings, icon: BuildingOffice2Icon },
    { id: 'rewards', name: pa.rewardsSettings, icon: GiftIcon },
    { id: 'pipeline', name: pa.pipelineSettings, icon: ServerStackIcon },
    { id: 'operations', name: pa.operationsSettings, icon: WrenchScrewdriverIcon },
    { id: 'notifications', name: st.notifications, icon: BellIcon },
    { id: 'privacy', name: st.privacy, icon: ShieldCheckIcon },
    { id: 'appearance', name: st.appearance, icon: PaintBrushIcon },
    { id: 'platform', name: pa.platformDefaults, icon: SparklesIcon },
  ];

  const handleChange = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const handleSystemSettingsSubmit = async () => {
    if (settings.system.newPassword && settings.system.newPassword !== settings.system.confirmPassword) {
      toast.error(st.passwordsDoNotMatch || 'Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const payload = { name: settings.system.name };
      if (settings.system.newPassword) {
        payload.currentPassword = settings.system.currentPassword;
        payload.newPassword = settings.system.newPassword;
      }
      await api.put('/user/update-profile', payload);
      await refreshUser();
      setSettings((prev) => ({
        ...prev,
        system: { ...prev.system, currentPassword: '', newPassword: '', confirmPassword: '' },
      }));
      toast.success(`${st.systemSettings} ${st.savedSuccessfully}`);
    } catch (error) {
      toast.error(error.response?.data?.error || st.failedUpdate);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    setPrivacySaving(true);
    try {
      const response = await api.put('/user/privacy-settings', settings.privacy);
      if (response.data?.privacy) {
        setSettings((prev) => ({
          ...prev,
          privacy: { ...prev.privacy, ...response.data.privacy },
        }));
      }
      toast.success(`${st.privacy} ${st.savedSuccessfully}`);
    } catch (error) {
      toast.error(error.response?.data?.error || st.failedUpdate);
    } finally {
      setPrivacySaving(false);
    }
  };

  const companyOptions = companies.map((company) => ({
    value: String(company.id),
    label: company.name,
  }));

  const renderCompanyScopedHint = () => (
    <div className="mb-6 rounded-2xl border border-orange-200/60 bg-orange-50/40 p-4  ">
      <label className="mb-2 block text-sm font-medium text-secondary-700 ">
        {pa.selectCompanyContext}
      </label>
      <Select
        value={selectedCompanyId ? String(selectedCompanyId) : ''}
        onChange={(event) => {
          const nextId = event.target.value ? Number(event.target.value) : null;
          setSelectedCompanyId(nextId);
          onCompanyConfigured?.(nextId);
        }}
        className="max-w-md"
      >
        <option value="">{pa.chooseCompany}</option>
        {companyOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Select>
      <p className="mt-2 text-xs text-secondary-500 ">{pa.companyContextHint}</p>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-2 text-lg font-semibold text-secondary-900 ">{st.systemSettings}</h3>
              <p className="mb-6 text-sm text-secondary-600 ">{pa.accountSettingsDesc}</p>
              <div className="space-y-4">
                <div>
                  <label className="form-label">{st.name}</label>
                  <Input
                    value={settings.system.name}
                    onChange={(event) => handleChange('system', 'name', event.target.value)}
                    placeholder={st.yourName}
                  />
                </div>
                <div className="space-y-4 border-t border-secondary-200 pt-4 ">
                  <h4 className="font-semibold text-secondary-900 ">{st.changePassword}</h4>
                  <Input type="password" value={settings.system.currentPassword} onChange={(event) => handleChange('system', 'currentPassword', event.target.value)} placeholder={st.enterCurrentPassword} autoComplete="current-password" />
                  <Input type="password" value={settings.system.newPassword} onChange={(event) => handleChange('system', 'newPassword', event.target.value)} placeholder={st.enterNewPassword} autoComplete="new-password" />
                  <Input type="password" value={settings.system.confirmPassword} onChange={(event) => handleChange('system', 'confirmPassword', event.target.value)} placeholder={st.confirmNewPasswordPlaceholder} autoComplete="new-password" />
                </div>
              </div>
            </div>
            <Button onClick={handleSystemSettingsSubmit} disabled={isLoading}>{st.saveChanges}</Button>
          </div>
        );
      case 'profile':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{st.profile}</h3>
            <p className="text-sm text-secondary-600 ">{pa.profileSettingsDesc}</p>
            <div className="rounded-2xl border border-secondary-200/80 bg-secondary-50/60 p-4  ">
              <p className="text-sm text-secondary-700 "><strong>{user?.username}</strong></p>
              <p className="text-sm text-secondary-500 ">{user?.email}</p>
              <p className="mt-2 text-xs text-secondary-500 ">{pa.superAdminBadge}</p>
            </div>
          </div>
        );
      case 'companies':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.companiesSettings}</h3>
            <p className="text-sm text-secondary-600 ">{pa.companiesSettingsDesc}</p>
            <PlatformCompaniesView
              companies={companies}
              loading={companiesLoading}
              variant="embedded"
              onConfigureCompany={(companyId) => {
                setSelectedCompanyId(companyId);
                setActiveTab('rewards');
                onCompanyConfigured?.(companyId);
              }}
              onViewCompany={(companyId) => onNavigateView?.('companyDetail', { companyId })}
            />
          </div>
        );
      case 'rewards':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.rewardsSettings}</h3>
            <p className="text-sm text-secondary-600 ">{pa.rewardsSettingsDesc}</p>
            {renderCompanyScopedHint()}
            {!selectedCompanyId ? (
              <p className="rounded-xl border border-dashed border-secondary-300 p-6 text-sm text-secondary-500  ">{pa.pickCompanyFirst}</p>
            ) : (
              <RewardsConfig key={`rewards-${selectedCompanyId}`} presetCompanyId={selectedCompanyId} />
            )}
          </div>
        );
      case 'pipeline':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.pipelineSettings}</h3>
            <p className="text-sm text-secondary-600 ">{pa.pipelineSettingsDesc}</p>
            {renderCompanyScopedHint()}
            {selectedCompanyId ? <AdminSystemConfig key={`pipeline-${selectedCompanyId}`} presetCompanyId={selectedCompanyId} /> : (
              <p className="rounded-xl border border-dashed border-secondary-300 p-6 text-sm text-secondary-500  ">{pa.pickCompanyFirst}</p>
            )}
          </div>
        );
      case 'operations':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.operationsSettings}</h3>
            <p className="text-sm text-secondary-600 ">{pa.operationsSettingsDesc}</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                { title: pa.operationsImports, desc: pa.operationsImportsDesc, action: () => onNavigateView?.('users', { companyId: selectedCompanyId }) },
                { title: pa.operationsGifts, desc: pa.operationsGiftsDesc, action: () => onNavigateView?.('pendingGifts', { companyId: selectedCompanyId }) },
                { title: pa.operationsReviews, desc: pa.operationsReviewsDesc, action: () => onNavigateView?.('vendorReviews', { companyId: selectedCompanyId }) },
                { title: pa.operationsAnalytics, desc: pa.operationsAnalyticsDesc, action: () => onNavigateView?.('analytics', { companyId: selectedCompanyId }) },
              ].map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={item.action}
                  className="rounded-2xl border border-secondary-200/80 bg-white p-4 text-left transition hover:border-orange-200 hover:bg-orange-50/30   "
                >
                  <p className="font-semibold text-secondary-900 ">{item.title}</p>
                  <p className="mt-1 text-sm text-secondary-500 ">{item.desc}</p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-orange-600 ">{pa.openSection}</p>
                </button>
              ))}
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{st.notificationPreferences}</h3>
            <div className="rounded-2xl border border-dashed border-secondary-300 bg-secondary-50/50 p-6  ">
              <p className="font-medium text-secondary-900 ">{pa.notificationsComingSoon}</p>
              <p className="mt-2 text-sm text-secondary-500 ">{pa.notificationsComingSoonDesc}</p>
            </div>
          </div>
        );
      case 'privacy':
        return privacyLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary-100 " />
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{st.privacy}</h3>
            <div className="space-y-3">
              {Object.entries(settings.privacy).map(([key, value]) => (
                <div key={key} className="toggle-row">
                  <span className="text-sm font-medium capitalize text-secondary-700 ">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <Switch checked={value} onChange={(checked) => handleChange('privacy', key, checked)} />
                </div>
              ))}
            </div>
            <Button onClick={handleSavePrivacy} disabled={privacySaving}>{st.saveChanges}</Button>
          </div>
        );
      case 'appearance':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{st.appearance}</h3>
            <label className="block text-sm font-medium text-secondary-700 ">{st.language || 'Language'}</label>
            <Select value={language} onChange={(event) => changeLanguage(event.target.value)} className="max-w-xs">
              <option value="en">English</option>
              <option value="es">Español</option>
            </Select>
          </div>
        );
      case 'platform':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.platformDefaults}</h3>
            <p className="text-sm text-secondary-600 ">{pa.platformDefaultsDesc}</p>
            <div className="rounded-2xl border border-secondary-200/80 bg-white p-4  ">
              <div className="flex items-start gap-3">
                <Cog6ToothIcon className="mt-0.5 h-5 w-5 text-orange-500" />
                <div>
                  <p className="font-semibold text-secondary-900 ">{pa.globalControlCenter}</p>
                  <p className="mt-1 text-sm text-secondary-500 ">{pa.globalControlCenterDesc}</p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="card-premium overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        <div className="border-b border-secondary-200/70 bg-secondary-50/60 p-4   lg:border-b-0 lg:border-r">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-secondary-500 ">{pa.settingsHub}</p>
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
                    isActive
                      ? 'bg-gradient-to-r from-orange-500/10 to-amber-500/10 text-orange-700 '
                      : 'text-secondary-600 hover:bg-white/70  '
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="p-6 max-lg:p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              {renderTabContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default PlatformSettingsPanel;
